import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { lineClampClass } from './lineClamp';
import { useCardMetrics } from './useCardMetrics';

export type ClampedTextProps = {
  // Undefined leaves the content unclamped and never shows the toggle; a read-only
  // long-form field (Body, TextArea, Message, Reason) passes its own `lines`/`clamp`
  // prop straight through
  lines: number | undefined;
  className?: string;
  disclosure?: 'toggle' | 'reason';
  children: ReactNode;
};

// Clamps `children` to `lines` (through the shared LINE_CLAMP classes) and shows a
// local "Show more" / "Show less" text button, but only when the content overflows
// that clamp or is expanded. Overflow is `scrollHeight > clientHeight` after layout,
// kept current with a ResizeObserver where there is one.
//
// The measurement is a `useLayoutEffect`: it reads layout that exists only after
// this render commits, and running before paint avoids a flash of the wrong
// "Show more" state.
export function ClampedText({
  lines,
  className,
  children,
  disclosure = 'toggle',
}: ClampedTextProps) {
  const reducedMotion = useReducedMotion();
  const metrics = useCardMetrics();
  const [height, setHeight] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (lines === undefined || expanded || !element) return;

    const measure = () => {
      // A fitting box still reads a stray rounding pixel of scrollHeight, so
      // require more than that before calling it overflow
      setOverflowing(element.scrollHeight - element.clientHeight > 1);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, lines, expanded]);

  const clamped = lines !== undefined && !expanded;
  const showsToggle =
    lines !== undefined &&
    (overflowing || expanded) &&
    !(disclosure === 'reason' && expanded);

  return (
    <div>
      <motion.div
        initial={false}
        animate={{ height: height ?? 'auto' }}
        onAnimationComplete={() => {
          if (expanded) setHeight(undefined);
        }}
        transition={{ duration: reducedMotion ? 0 : 0.18 }}
        className="overflow-hidden"
      >
        <div
          ref={ref}
          className={cn(className, clamped && lineClampClass(lines))}
        >
          {children}
        </div>
      </motion.div>
      <AnimatePresence initial={false}>
        {showsToggle ? (
          <motion.button
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.12 }}
            type="button"
            onClick={() => {
              if (disclosure === 'reason' && ref.current)
                setHeight(ref.current.scrollHeight);
              setExpanded((current) => !current);
            }}
            className={cn(
              'mt-[var(--ac-gap-xs)] text-[length:var(--ac-meta)] text-foreground-muted underline underline-offset-2 transition-opacity duration-[120ms] hover:text-foreground motion-reduce:transition-none',
              metrics.mobile && 'min-h-[calc(32*var(--ac-u))]'
            )}
          >
            {disclosure === 'reason'
              ? 'More'
              : expanded
                ? 'Show less'
                : 'Show more'}
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
